/**
 * Dispositivos monitorados do cliente (Monitoramento de Dispositivos).
 *
 * Até 2026-10-21 esta aba lia GET /devices — os aparelhos de notificação push
 * do PRÓPRIO usuário — e criava/editava em rotas inexistentes. Agora usa o
 * recurso canônico (/device-monitoring/devices) e o formulário canônico
 * (DeviceForm), com as ações decididas pelo backend (useCapabilities).
 * Excluir = desativar, como no módulo.
 */
import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Plus, Pencil, Power, Cpu, Search } from "lucide-react";
import { useCapabilities } from "@/hooks/useCapabilities";
import DeviceForm from "@/pages/DeviceMonitoring/DeviceForm";
import { TYPE_OPTIONS } from "@/pages/DeviceMonitoring/options";

const TYPE_LABELS = Object.fromEntries(TYPE_OPTIONS.map((t) => [t.value, t.label]));
const MODULE = "monitoramento_dispositivos";

export default function DevicesTab({ clientId }) {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState("");
  const caps = useCapabilities(
    [`${MODULE}.view`, `${MODULE}.create`, `${MODULE}.edit`, `${MODULE}.delete`],
    { clientId },
  );
  const canView = caps.can(`${MODULE}.view`);

  const loadDevices = useCallback(async () => {
    if (!canView) {
      setDevices([]);
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.get("/device-monitoring/devices", { params: { client_id: clientId, limit: 1000 } });
      setDevices(Array.isArray(data) ? data : data?.items || []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Erro ao carregar dispositivos");
    } finally {
      setLoading(false);
    }
  }, [clientId, canView]);

  useEffect(() => { loadDevices(); }, [loadDevices]);

  const openForm = (device) => { setEditing(device); setShowForm(true); };
  const closeForm = () => { setShowForm(false); setEditing(null); };

  const handleDeactivate = async (device) => {
    if (!confirm(`Desativar o dispositivo ${device.code}?`)) return;
    try {
      await api.delete(`/device-monitoring/devices/${device.id}`);
      toast.success("Dispositivo desativado");
      loadDevices();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Erro ao desativar");
    }
  };

  if (caps.loading || loading) return <Skeleton className="h-40 w-full" />;

  if (!canView) {
    const blocked = caps.get(`${MODULE}.view`);
    return (
      <div className="text-center py-8 text-muted-foreground" data-testid="devices-unavailable">
        <Cpu className="h-12 w-12 mx-auto mb-3 opacity-40" />
        <p className="text-sm">Monitoramento de Dispositivos indisponível neste contexto.</p>
        {blocked?.message && <p className="text-xs mt-1">{blocked.message}</p>}
      </div>
    );
  }

  const filtered = devices.filter((d) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return [d.code, d.location, d.area_name, d.unit_name].some((v) => (v || "").toLowerCase().includes(q));
  });

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant="outline">{filtered.length} dispositivo(s)</Badge>
        {caps.can(`${MODULE}.create`) && (
          <Button size="sm" onClick={() => openForm(null)} data-testid="devices-new">
            <Plus className="h-4 w-4 mr-1" />Novo Dispositivo
          </Button>
        )}
        <div className="relative">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Buscar dispositivo..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-8 h-9 text-sm w-48" />
        </div>
      </div>
      {filtered.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-8">Nenhum dispositivo cadastrado</p>
      ) : (
        <div className="divide-y divide-border border border-border rounded-md">
          {filtered.map((d) => (
            <div key={d.id} className="flex items-center justify-between p-3" data-testid={`device-row-${d.id}`}>
              <div className="flex items-center gap-3">
                <Cpu className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium">{d.code}</p>
                  <p className="text-xs text-muted-foreground">
                    {TYPE_LABELS[d.type_key] || d.type_key} · {[d.unit_name, d.area_name, d.subarea_name].filter(Boolean).join(" › ") || "—"}
                  </p>
                </div>
              </div>
              <div className="flex gap-1">
                {caps.can(`${MODULE}.edit`) && (
                  <Button size="icon" variant="ghost" onClick={() => openForm(d)} data-testid={`device-edit-${d.id}`}>
                    <Pencil className="h-3 w-3" />
                  </Button>
                )}
                {caps.can(`${MODULE}.delete`) && d.active !== false && (
                  <Button size="icon" variant="ghost" onClick={() => handleDeactivate(d)} title="Desativar" data-testid={`device-deactivate-${d.id}`}>
                    <Power className="h-3 w-3 text-destructive" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      {showForm && (
        <DeviceForm
          defaultClientId={clientId}
          device={editing}
          onClose={closeForm}
          onSave={() => { closeForm(); loadDevices(); }}
        />
      )}
    </div>
  );
}
